import React, { CSSProperties } from "react";
import ct from "class-types.macro";
import camelcase from "lodash/camelCase";

import ConnectForm from "./ConnectForm";
import {
  inputBase,
  inputError,
  inputLabel,
  selectInputIcon,
  selectInputIconNoLabel,
} from "./inputStyles";
import Icon from "~/common/Icons/Standard";
import InputError from "./InputError";

type SelectProps = {
  label?: string;
  id?: string;
  name?: string;
  options: readonly {
    value: string | number;
    text: string;
    group?: {
      name: string;
    };
  }[];
  clearErrorsOnFocus?: boolean;
  defaultValue?: string | number;
  displayErrorMessage?: boolean;
  placeholder?: string;
  noPlaceholder?: boolean;
  errorText?: string;
  labelClassName?: string;
  svgClassName?: string;
  required?: boolean;
  showRequired?: boolean;
  errorPosition?: string;
  errorStyles?: CSSProperties;
  selectErrorClass?: string | null;
  overrideDefaultErrorPositioning?: boolean;
  containerClassName?: string;
  hiddenLabel?: boolean;
  style?: CSSProperties;
  iconStyle?: CSSProperties;
  selectErrorStyles?: CSSProperties;
  selectClassName?: string | null;
  placeHolderClassName?: string;
  dataTestId?: string;
  requiredAttribute?: boolean;
};

const SelectInput = ({
  label,
  name = camelcase(label),
  id = name,
  options,
  defaultValue = "",
  displayErrorMessage = true,
  placeholder = `Select ${label}`,
  noPlaceholder = false,
  errorText = label || "This field",
  labelClassName = inputLabel,
  svgClassName = "",
  required = false,
  showRequired = true,
  errorPosition,
  errorStyles,
  selectErrorClass = null,
  overrideDefaultErrorPositioning = false,
  containerClassName,
  hiddenLabel,
  style,
  iconStyle,
  selectErrorStyles = {},
  selectClassName = null,
  placeHolderClassName = "[&:not(:placeholder-shown)]:pr-10",
  dataTestId = "",
  clearErrorsOnFocus = true,
  requiredAttribute = false,
}: SelectProps) => {
  return (
    <ConnectForm>
      {({ register, errors, clearErrors }) => {
        const selectErrorStyling = errors[name] ? selectErrorStyles : null;
        return (
          <div
            className={`${ct("relative")} ${containerClassName || ct("pb-5")}`}
          >
            {label ? (
              <label htmlFor={id} className={labelClassName || inputLabel}>
                {label}{" "}
                {required && showRequired ? (
                  <span className={ct("text-red-500")}>*</span>
                ) : (
                  ""
                )}
              </label>
            ) : null}
            <select
              id={id}
              className={`${selectClassName ?? inputBase} ${
                errors[name] ? `${inputError} ${selectErrorClass}` : ""
              } ${placeHolderClassName}`}
              style={{
                height: "44px",
                ...style,
                ...selectErrorStyling,
              }}
              {...register(name)}
              defaultValue={defaultValue}
              onFocus={() => {
                if (clearErrorsOnFocus) {
                  clearErrors(name);
                }
              }}
              data-testid={dataTestId}
              required={requiredAttribute}
              aria-describedby={`${name}Error`}
              aria-label={hiddenLabel ? label : undefined}
            >
              {!defaultValue && !noPlaceholder && (
                <option value="" disabled>
                  {placeholder}
                </option>
              )}

              {Array.from(
                new Set(options.map(({ group }) => group?.name || "")),
              ).map((name: string) => {
                const groupOptions = options.filter(
                  ({ group }) => group && group.name === name,
                );

                if (name.length > 0 && groupOptions.length > 0) {
                  return (
                    <optgroup key={name} label={name}>
                      {groupOptions.map(({ value, text }) => (
                        <option key={value} value={value}>
                          {text}
                        </option>
                      ))}
                    </optgroup>
                  );
                }

                return options
                  .filter(({ group }) => group === undefined)
                  .map(({ value, text }) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ));
              })}
            </select>
            <InputError
              errors={errors}
              label={errorText ?? (label || "This field")}
              name={name}
              displayErrorMessage={displayErrorMessage}
              errorPosition={errorPosition}
              styles={errorStyles}
              overridePosition={overrideDefaultErrorPositioning}
            />
            <Icon
              name="caret-down"
              width="40"
              height="17"
              className={`${
                label && !hiddenLabel ? selectInputIcon : selectInputIconNoLabel
              } ${svgClassName}`}
              style={iconStyle}
            />
          </div>
        );
      }}
    </ConnectForm>
  );
};

export default SelectInput;
